import type {
  AuthRequest,
  AuthResponseDto,
  AvailabilityDto,
  CreateReservationRequest,
  ErrorBodyDto,
  PagedResponseDto,
  ReservationDto,
  RestaurantDto,
  ReviewDto,
  TelegramLinkDto,
  TelegramStatusDto,
  UpdateReservationRequest,
  UserDto,
} from '../dto';
import type { ApiResponse, ApiService, RestaurantsQuery, SearchQuery } from './apiService';

/**
 * Cliente HTTP del contrato real (docs/ANALISIS.md §9).
 *
 * No lanza por un código de error: devuelve `ApiResponse` con el estado, para que el
 * repositorio decida — igual que hace el cliente Android con `Response<T>` de Retrofit. Las
 * únicas excepciones que salen de aquí son las de transporte, que el repositorio traduce con
 * `ErrorMapper.fromThrowable`.
 */
export class HttpApi implements ApiService {
  constructor(
    private readonly baseUrl: string,
    private readonly tokenProvider: () => string | null,
    private readonly timeoutMs = 15_000,
  ) {}

  private async request<T>(
    method: string,
    path: string,
    options: { query?: Record<string, unknown>; body?: unknown; headers?: Record<string, string> } = {},
  ): Promise<ApiResponse<T>> {
    // `new URL` exige una base cuando la ruta es relativa, y `/api/v1` lo es: sin el origen
    // del documento lanzaría "Invalid URL" y ninguna petición llegaría a salir.
    const target = `${this.baseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
    const url = new URL(target, typeof window === 'undefined' ? undefined : window.location.origin);
    for (const [key, value] of Object.entries(options.query ?? {})) {
      if (value == null) continue;
      // Los filtros multivalor se repiten como `?cuisine=A&cuisine=B`, igual que en Retrofit.
      if (Array.isArray(value)) value.forEach((v) => url.searchParams.append(key, String(v)));
      else url.searchParams.set(key, String(value));
    }

    const token = this.tokenProvider();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const response = await fetch(url, {
        method,
        signal: controller.signal,
        headers: {
          Accept: 'application/json',
          ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...options.headers,
        },
        body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      });

      if (response.status === 204) return { status: 204, body: null };

      const text = await response.text();
      const parsed: unknown = text.length > 0 ? safeParse(text) : null;

      if (!response.ok) {
        const errorBody = parsed as ErrorBodyDto | null;
        return { status: response.status, body: null, fields: errorBody?.fields ?? undefined };
      }
      return { status: response.status, body: parsed as T };
    } finally {
      clearTimeout(timer);
    }
  }

  getRestaurants(query: RestaurantsQuery = {}): Promise<ApiResponse<PagedResponseDto<RestaurantDto>>> {
    return this.request('GET', 'restaurants', {
      query: {
        lat: query.latitude,
        lng: query.longitude,
        location: query.locationId,
        collection: query.collection,
        page: query.page ?? 0,
        size: query.size ?? 20,
      },
    });
  }

  getRestaurant(id: string): Promise<ApiResponse<RestaurantDto>> {
    return this.request('GET', `restaurants/${encodeURIComponent(id)}`);
  }

  getReviews(id: string, page = 0, size = 10): Promise<ApiResponse<PagedResponseDto<ReviewDto>>> {
    return this.request('GET', `restaurants/${encodeURIComponent(id)}/reviews`, { query: { page, size } });
  }

  searchRestaurants(query: SearchQuery): Promise<ApiResponse<PagedResponseDto<RestaurantDto>>> {
    return this.request('GET', 'restaurants/search', {
      query: {
        q: query.query,
        location: query.locationId,
        lat: query.latitude,
        lng: query.longitude,
        cuisine: query.cuisines,
        price: query.priceLevels,
        min_rating: query.minRating,
        max_distance: query.maxDistanceMeters,
        amenity: query.amenities,
        only_available: query.onlyAvailable ?? false,
        date: query.date,
        time: query.time,
        party: query.partySize,
        sort: query.sort,
        page: query.page ?? 0,
        size: query.size ?? 20,
      },
    });
  }

  getAvailability(
    id: string,
    date: string,
    time: string,
    partySize: number,
  ): Promise<ApiResponse<AvailabilityDto>> {
    return this.request('GET', `restaurants/${encodeURIComponent(id)}/availability`, {
      query: { date, time, party: partySize },
    });
  }

  createReservation(
    idempotencyKey: string,
    body: CreateReservationRequest,
  ): Promise<ApiResponse<ReservationDto>> {
    // La clave de idempotencia viaja como cabecera: reintentar el mismo borrador tras un
    // timeout devuelve la reserva ya creada en lugar de duplicarla.
    return this.request('POST', 'reservations', {
      body,
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  getReservations(status: string | null = null): Promise<ApiResponse<ReservationDto[]>> {
    return this.request('GET', 'reservations', { query: { status } });
  }

  getReservation(id: string): Promise<ApiResponse<ReservationDto>> {
    return this.request('GET', `reservations/${encodeURIComponent(id)}`);
  }

  updateReservation(id: string, body: UpdateReservationRequest): Promise<ApiResponse<ReservationDto>> {
    return this.request('PUT', `reservations/${encodeURIComponent(id)}`, { body });
  }

  cancelReservation(id: string): Promise<ApiResponse<void>> {
    return this.request('DELETE', `reservations/${encodeURIComponent(id)}`);
  }

  startTelegramLink(name: string, returnUrl?: string): Promise<ApiResponse<TelegramLinkDto>> {
    // Se manda a dónde volver para que el chat pueda ofrecer el botón de regreso al punto
    // exacto del que salió. El servidor solo la acepta si coincide con este mismo origen.
    return this.request('POST', 'auth/telegram/start', { body: { name, return_url: returnUrl } });
  }

  telegramLinkStatus(token: string): Promise<ApiResponse<TelegramStatusDto>> {
    return this.request('GET', 'auth/telegram/status', { query: { token } });
  }

  identify(body: AuthRequest): Promise<ApiResponse<AuthResponseDto>> {
    return this.request('POST', 'auth/identify', { body });
  }

  getProfile(): Promise<ApiResponse<UserDto>> {
    return this.request('GET', 'me');
  }

  updateProfile(body: UserDto): Promise<ApiResponse<UserDto>> {
    return this.request('PUT', 'me', { body });
  }
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    // Un cuerpo que no es JSON no debe tumbar la pantalla: el repositorio lo verá como body
    // nulo y lo clasificará como error desconocido.
    return null;
  }
}
